import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Store,
  MailPlus,
  HardDrive,
  Rocket,
  UserRound,
  Tag,
  Clapperboard,
  ClipboardList,
  BarChart3,
  ShieldCheck,
  Users,
  Settings,
  type LucideIcon,
} from 'lucide-react';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';

type NavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  isActive?: (pathname: string) => boolean;
};

const NAV_ITEMS: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, end: true },
  {
    to: '/merchants',
    label: 'Merchants',
    icon: Store,
    isActive: (pathname) =>
      pathname === '/merchants' ||
      (pathname.startsWith('/merchants/') &&
        !pathname.startsWith('/merchants/invitations') &&
        !pathname.startsWith('/merchants/onboard')),
  },
  { to: '/merchants/invitations', label: 'Invitations', icon: MailPlus, end: true },
  { to: '/devices', label: 'Devices', icon: HardDrive },
  { to: '/agent-releases', label: 'Agent releases', icon: Rocket, end: true },
  { to: '/consumers', label: 'Consumers', icon: UserRound },
  { to: '/deals', label: 'Deals', icon: Tag },
  { to: '/reels', label: 'Reels', icon: Clapperboard },
  // No top-level "Receipts": receipts belong to a merchant — you reach a
  // store's receipts by opening that merchant (drill-in from its detail
  // page). "Parse Review" below is the platform-operator pipeline tool for
  // the receipts that fail to parse; the cross-merchant browser at
  // /receipts stays reachable as a support lookup, just not a nav feature.
  { to: '/parse-review', label: 'Parse Review', icon: ClipboardList, end: true },
  { to: '/metrics', label: 'Metrics', icon: BarChart3, end: true },
  { to: '/audit', label: 'Audit', icon: ShieldCheck, end: true },
  { to: '/users', label: 'Users', icon: Users, end: true },
  { to: '/settings', label: 'Settings', icon: Settings, end: true },
];

export function SideNav() {
  const { pathname } = useLocation();

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem className='flex gap-2 items-center'>
            <div className="flex aspect-square size-8 items-center justify-center rounded-md bg-sidebar-primary text-sm font-semibold text-sidebar-primary-foreground">
              d
            </div>
            <span className="truncate text-sm font-semibold group-data-[collapsible=icon]:hidden">
              digipop admin
            </span>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu className='gap-1'>
            {NAV_ITEMS.map(({ to, label, icon: Icon, end, isActive }) => {
              const active =
                isActive?.(pathname) ??
                (end ? pathname === to : pathname === to || pathname.startsWith(`${to}/`));

              return (
                <SidebarMenuItem key={to}>
                  <SidebarMenuButton
                    asChild
                    isActive={active}
                    tooltip={label}
                  >
                    <NavLink to={to} end={end}>
                      <Icon />
                      <span className="group-data-[collapsible=icon]:hidden">{label}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}
